import React, { useState, useEffect, forwardRef } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap';
import CalendarIcon from 'mdi-react/CalendarIcon';

const TimePeriodFilterPublished = ({
  setValue,
  isLoading,
  selectedButtonInitial,
  startDateInitial,
  endDateInitial,
}) => {
  const [showCustom, setShowCustom] = useState(selectedButtonInitial === 'custom');
  const [selectedButton, setSelectedButton] = useState(selectedButtonInitial || 'this_month');
  const [startDate, setStartDate] = useState(startDateInitial ? moment(startDateInitial).toDate() : moment().startOf('month').toDate());
  const [endDate, setEndDate] = useState(endDateInitial ? moment(endDateInitial).toDate() : moment().endOf('month').toDate());

  // Sync local state with prop changes (e.g., when view type changes)
  useEffect(() => {
    if (selectedButtonInitial) {
      setSelectedButton(selectedButtonInitial);
      setShowCustom(selectedButtonInitial === 'custom');
    }
    if (startDateInitial) {
      setStartDate(moment(startDateInitial).toDate());
    }
    if (endDateInitial) {
      setEndDate(moment(endDateInitial).toDate());
    }
  }, [selectedButtonInitial, startDateInitial, endDateInitial]);

  const selectDateRange = (range) => {
    let dateFrom;
    let dateTo;
    switch (range) {
      case 'last_90_days':
        setShowCustom(false);
        dateFrom = moment().subtract(90, 'days').startOf('day').toDate();
        dateTo = moment().endOf('day').toDate();
        break;
      case 'last_month':
        setShowCustom(false);
        dateFrom = moment().subtract(1, 'month').startOf('month').toDate();
        dateTo = moment().subtract(1, 'month').endOf('month').toDate();
        break;
      case 'this_month':
        setShowCustom(false);
        dateFrom = moment().startOf('month').toDate();
        dateTo = moment().endOf('month').toDate(); // Full month
        break;
      case 'custom':
        setShowCustom(true);
        dateFrom = moment().startOf('year').toDate();
        dateTo = moment().endOf('year').toDate();
        break;
      default:
        dateFrom = moment().subtract(90, 'days').startOf('day').toDate();
        dateTo = moment().endOf('day').toDate();
        break;
    }
    setStartDate(dateFrom);
    setEndDate(dateTo);
    setValue('startDate', dateFrom, range);
    setValue('endDate', dateTo, range);
    setSelectedButton(range);
  };

  const getText = () => {
    let dateText = '';
    if (
      moment(startDate).format('M') === moment(endDate).format('M') &&
      moment(startDate).format('D') === moment(endDate).format('D') &&
      moment(startDate).format('YYYY') === moment(endDate).format('YYYY')
    ) {
      dateText = moment(startDate).format('MMM D, YYYY');
    } else {
      dateText = `${moment(startDate).format('MMM D, YYYY')}  —  ${moment(endDate).format('MMM D, YYYY')}`;
    }
    return dateText;
  };

  const buttonClass = 'btn-sm mr-0 time-period-filter-buttons-extended';

  // eslint-disable-next-line react/prop-types
  const DatePickerCustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
    <div className="form-group mb-2">
      <InputGroup size="md">
        <input
          type="text"
          className="form-control form-control-sm"
          value={value}
          onClick={onClick}
          onChange={onChange}
          style={{
            border: '1px solid #ced4da',
            borderRadius: '5px',
          }}
          ref={ref}
        />
        <InputGroupAddon
          addonType="append"
          style={{
            padding: '3px 0 3px 3px',
          }}
        >
          <CalendarIcon
            className="date-picker-icon"
            onClick={onClick}
          />
        </InputGroupAddon>
      </InputGroup>
    </div>
  ));

  return (
    <div>
      <Row style={{ minWidth: '300px' }}>
        <Col lg="auto">
          <Button
            onClick={() => selectDateRange('last_90_days')}
            className={selectedButton === 'last_90_days' ? `active ${buttonClass}` : buttonClass}
            style={{ borderRadius: '5px 0 0 5px', borderRight: '0', minWidth: '115px' }}
            size="sm"
            disabled={isLoading}
          >
            Last 90 Days
          </Button>
          <Button
            onClick={() => selectDateRange('last_month')}
            className={selectedButton === 'last_month' ? `active ${buttonClass}` : buttonClass}
            style={{ borderRadius: '0', borderRight: '0' }}
            size="sm"
            disabled={isLoading}
          >
            Last Month
          </Button>
          <Button
            onClick={() => selectDateRange('this_month')}
            className={selectedButton === 'this_month' ? `active ${buttonClass}` : buttonClass}
            style={{ borderRadius: '0', borderRight: '0' }}
            size="sm"
            disabled={isLoading}
          >
            This Month
          </Button>
          <Button
            onClick={() => selectDateRange('custom')}
            className={selectedButton === 'custom' ? `active ${buttonClass}` : buttonClass}
            style={{ borderRadius: '0 5px 5px 0', borderColor: '#4A69F2' }}
            size="sm"
            disabled={isLoading}
          >
            Custom
          </Button>
        </Col>
        <Col>
          {showCustom ? (
            <Row>
              <Col xs="auto" className="">
                <div style={{ maxWidth: '150px' }}>
                  <DatePicker
                    id="startDateDatePicker"
                    selected={startDate}
                    onChange={date => setStartDate(date) || setValue('startDate', date)}
                    dateFormat="MM/dd/yyyy"
                    maxDate={new Date()}
                    placeholderText="Start Date"
                    dropDownMode="select"
                    customInput={<DatePickerCustomInput />}
                    disabled={isLoading}
                  />
                </div>
              </Col>
              <Col xs="auto" className="">
                <div style={{ maxWidth: '150px' }}>
                  <DatePicker
                    id="endDateDatePicker"
                    selected={endDate}
                    onChange={date => setEndDate(date) || setValue('endDate', date)}
                    dateFormat="MM/dd/yyyy"
                    maxDate={new Date()}
                    placeholderText="End Date"
                    dropDownMode="select"
                    customInput={<DatePickerCustomInput />}
                    disabled={isLoading}
                  />
                </div>
              </Col>
              <Col />
            </Row>
          ) : null}
        </Col>
      </Row>
      <Row style={{ minWidth: '300px' }}>
        <Col lg="auto">
          <h5 style={{ color: 'black', marginBottom: '8px' }} className="bold-text">{getText()}</h5>
        </Col>
      </Row>
    </div>
  );
};

TimePeriodFilterPublished.propTypes = {
  setValue: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  selectedButtonInitial: PropTypes.string,
  startDateInitial: PropTypes.string,
  endDateInitial: PropTypes.string,
};

TimePeriodFilterPublished.defaultProps = {
  isLoading: false,
  selectedButtonInitial: 'this_month',
  startDateInitial: null,
  endDateInitial: null,
};

export default TimePeriodFilterPublished;
